import { useState } from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { countCellsSelected } from 'modules/shift_management/hooks/useCountCellsSelected'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import { useVisibleDefaultShifts } from 'modules/shift_management/hooks/useVisibleDefaultShifts'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import { trackAddShiftStartEvent } from 'modules/shift_management/lib/trackEvents'
import { type ShiftsRow } from 'modules/shift_management/lib/types'

import AddButton from '../AddButton'
import AddShiftSidepanel from '../AddShiftSidepanel'
import Content from './Content'

interface Props {
  date: moment.Moment
  shiftsRow: ShiftsRow
  contractVersions: ContractVersions
}

const QuickActionsPopover = ({ date, shiftsRow, contractVersions }: Props) => {
  const [open, toggle] = useToggle(false)
  const [openAddShiftSidePanel, setOpenAddShiftSidePanel] = useState<boolean>(false)
  const [{ view }] = useSchedule()
  const { daysByRow, showEmptyShiftCell, shifts, restDays } = useSelectionState()
  const dispatchSelection = useSelectionDispatch()

  const visibleDefaultShifts = useVisibleDefaultShifts({})

  const onOpenPopover = () => {
    toggle(true)()
    const selectedEmployees = Object.keys(daysByRow).length
    const impactedEmployees = selectedEmployees || 1
    const cellCount = countCellsSelected({ daysByRow, shifts, restDays, view })
    const impactedShifts = cellCount || 1
    dispatchSelection({
      type: 'SHOW_EMPTY_SHIFT_CELL',
    })
    // Forcing to enable the day if it's not selected.
    dispatchSelection({
      type: 'TOGGLE_DAY',
      day: date,
      shiftsRow,
      view,
      forcedValue: true,
    })
    trackAddShiftStartEvent({
      view,
      impactedEmployees,
      cellCount: impactedShifts,
      shiftType: ADD_SHIFT_OPTIONS.savedCustom,
      from: 'contextual_button',
      quickActions: true,
    })
  }

  const onClosePopover = () => {
    dispatchSelection({
      type: 'CLEAR_SELECTION',
    })
    toggle(false)()
  }

  const onOpenSidePanel = () => {
    dispatchSelection({
      type: 'HIDE_EMPTY_SHIFT_CELL',
    })
    toggle(false)()
    setOpenAddShiftSidePanel(true)
  }

  const onCloseSidePanel = () => {
    setOpenAddShiftSidePanel(false)
  }

  const onInteractOutside = () => {
    dispatchSelection({
      type: 'HIDE_EMPTY_SHIFT_CELL',
    })
    toggle(false)()
  }

  if (!visibleDefaultShifts.length) {
    return (
      // Stop propagation to avoid unselecting the cell on the background.
      <Box onClick={(ev) => ev.stopPropagation()}>
        <AddButton onClick={() => setOpenAddShiftSidePanel(true)} />
        {openAddShiftSidePanel && (
          <AddShiftSidepanel
            onClose={onCloseSidePanel}
            date={date}
            noSavedShifts
            contractVersions={contractVersions}
          />
        )}
      </Box>
    )
  }

  return (
    // Stop propagation to avoid unselecting the cell on the background.
    <Box onClick={(ev) => ev.stopPropagation()}>
      <PopoverMenu type="dialog" open={open}>
        <PopoverMenu.Trigger>
          {/* Hide contextual button when is assigning saved shifts from popover */}
          {!showEmptyShiftCell && <AddButton onClick={onOpenPopover} />}
        </PopoverMenu.Trigger>

        <PopoverMenu.Content
          side="right"
          align="center"
          width="s360"
          onInteractOutside={onInteractOutside}
        >
          <Box paddingY="s12" paddingX="s24" gap="s16" maxHeight="s400">
            <Content
              savedShifts={visibleDefaultShifts}
              onClose={onClosePopover}
              contractVersions={contractVersions}
            />
            <Box
              flexDirection="row"
              justifyContent="flexEnd"
              onClick={(ev) => ev.stopPropagation()}
            >
              <Button onClick={onOpenSidePanel} type="secondary" iconName="add">
                {i18n.t('shift_management.quick_actions.advanced_options')}
              </Button>
            </Box>
          </Box>
        </PopoverMenu.Content>
      </PopoverMenu>
      {openAddShiftSidePanel && (
        <Box onClick={(ev) => ev.stopPropagation()}>
          <AddShiftSidepanel
            onClose={onCloseSidePanel}
            date={date}
            advancedOptions
            contractVersions={contractVersions}
          />
        </Box>
      )}
    </Box>
  )
}

export default observer(QuickActionsPopover)
